import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { logout } from '@app/store/slices/token';
import { getConfig } from '@app/services/configService';

const DEFAULT_IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000;

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;

export const useIdleTimeout = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, accessToken } = useAppSelector((state) => state.token);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      return;
    }

    const { idleTimeoutMs } = getConfig();
    const timeoutMs = idleTimeoutMs ?? DEFAULT_IDLE_TIMEOUT_MS;

    const clearTimer = () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const handleExpiry = () => {
      clearTimer();
      void dispatch(logout({ skipSsoLogout: true })).finally(() => {
        window.location.replace('/auth/login');
      });
    };

    const resetTimer = () => {
      clearTimer();
      timerRef.current = window.setTimeout(handleExpiry, timeoutMs);
    };

    resetTimer();

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [accessToken, dispatch, isAuthenticated]);
};
